import { useState } from 'react';
import { httpClient } from '../api/http';
import { Alert } from '@piggytechng/component-library';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

const useFlexNaira = () => {
  const params = useParams();
  const userIdIn = params.id as string;

  const [transactionsParams, setTransactionsParams] = useState({
    page: 1,
    limit: 30,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [flexNairaTransaction, setFlexNairaTransaction] = useState();
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchedTransactions, setSearchedTransactions] = useState<any[] | null>(null);

  const {
    data: flexNairaBalance,
    isLoading: isLoadingFlexNairaBalance,
    refetch: refetchFlexNairaBalance,
  } = useQuery({
    queryKey: ['flex-naira-balance' + userIdIn],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: () => httpClient.get(`admin/flexnaira/balance/${userIdIn}`).then((res) => res.data.data?.balance),
  });

  const {
    data: flexNairaTransactions,
    isLoading: isLoadingFlexNairaTransactions,
    refetch: refetchFlexNairaTransactions,
  } = useQuery({
    queryKey: ['flex-naira-transactions' + userIdIn + transactionsParams.page + transactionsParams.limit],
    enabled: !!userIdIn,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: () =>
      httpClient
        .get(`admin/flexnaira/transactions/${userIdIn}/${transactionsParams.page}/${transactionsParams.limit}`)
        .then((res) => res.data.data?.transaction),
  });

  const {
    data: flexNairaAccountNumbers,
    isLoading: isLoadingFlexNairaAccountNumbers,
    refetch: refetchFlexNairaAccountNumbers,
  } = useQuery({
    queryKey: ['flex-naira-account-numbers' + userIdIn],
    retry: false,
    enabled: !!userIdIn,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: () =>
      httpClient.get(`admin/flexnaira/account-numbers/${userIdIn}`).then((res) => res.data?.data?.flexNubans),
  });

  const fetchFlexNairaTransaction = async (reference: string) => {
    try {
      setIsLoading(true);
      const url = `admin/flexnaira/transaction/${reference}`;
      const response: any = await httpClient.get(url);
      if (response.data.status) {
        setFlexNairaTransaction(response.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const searchFlexNairaTransactions = async (query: string, filter = 'reference', page = 1, limit = 30) => {
    if (!query.trim()) {
      Alert.error('Please enter a search value');
      return;
    }
    try {
      setIsSearching(true);
      const url = `admin/flexnaira/transactions/search/${userIdIn}/${filter}/${page}/${limit}`;
      const response: any = await httpClient.post(url, { query: query.trim() });
      if (response.data.status) {
        const data = response.data.data;
        const results = data?.transaction ?? data?.transactions ?? (Array.isArray(data) ? data : []);
        setSearchedTransactions(results ?? []);
      }
    } catch (error: any) {
      Alert.error(error?.response?.data?.message || 'Failed to search transactions');
    } finally {
      setIsSearching(false);
    }
  };

  const clearFlexNairaSearch = () => {
    setSearchedTransactions(null);
  };

  const updateTransactionMemo = async (reference: string, memo: string) => {
    try {
      setIsLoading(true);
      const url = `admin/flexnaira/transaction/update-memo/${reference}`;
      const response: any = await httpClient.post(url, { memo });
      if (response.data.status) {
        Alert.success(response.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const fixAccountBalance = async (userId: string) => {
    try {
      setIsLoading(true);
      const url = `admin/flexnaira/fix-account-balance/${userId}`;
      const response: any = await httpClient.post(url);
      if (response.data.status) {
        // const balance = response.data.balance;
        refetchFlexNairaBalance();
        // setFlexNairaBalance(balance);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteFlexNairaAccount = async (accountNumber: string, callback?: () => void) => {
    try {
      setIsLoading(true);
      const url = `admin/wallets/flexnaira/${userIdIn}/van`;
      const response: any = await httpClient.delete(url, { data: { accountNumber } });
      if (response.data.status) {
        Alert.success(response.data.message || 'Flex Naira account number deleted');
        refetchFlexNairaAccountNumbers();
        callback?.();
      }
    } catch (error: any) {
      Alert.error(error?.response?.data?.message || 'Failed to delete account number');
    } finally {
      setIsLoading(false);
    }
  };

  const generateFlexNairaNuban = async (callback?: () => void) => {
    try {
      setIsLoading(true);
      const url = `admin/flexnaira/generate-nuban/${userIdIn}`;
      const response: any = await httpClient.post(url);
      if (response.data.status) {
        Alert.success(response.data.message || 'Flex Naira account number generated');
        refetchFlexNairaAccountNumbers();
        callback?.();
      }
    } catch (error: any) {
      Alert.error(error?.response?.data?.message || 'Failed to generate account number');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    flexNairaBalance,
    isLoadingFlexNairaBalance,
    refetchFlexNairaBalance,
    flexNairaTransactions,
    isLoadingFlexNairaTransactions,
    refetchFlexNairaTransactions,
    flexNairaTransaction,
    flexNairaAccountNumbers,
    isLoadingFlexNairaAccountNumbers,
    refetchFlexNairaAccountNumbers,
    updateTransactionMemo,
    fixAccountBalance,
    deleteFlexNairaAccount,
    generateFlexNairaNuban,
    fetchFlexNairaTransaction,
    transactionsParams,
    setTransactionsParams,
    searchFlexNairaTransactions,
    clearFlexNairaSearch,
    searchedTransactions,
    isSearching,
  };
};

export default useFlexNaira;
